import {track} from '@getmoss/moss-analytics'
import {motion, useInView, useScroll, useTransform} from 'framer-motion'
import type {FC} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {useMossExchangeRate} from 'src/common/hooks/useMossExchangeRate'
import {CardHeader} from './components/CardHeader'
import {CountrySelector} from './components/CountrySelector'
import {DestinationSelector} from './components/DestinationSelector'
import {MealSelector} from './components/MealSelector'
import {ResultsDisplay} from './components/ResultsDisplay'
import {TripDetailsForm} from './components/TripDetailsForm'
import {DESTINATION_CURRENCIES, getAvailableDestinations} from './constants'
import type {
  CalcResult,
  Country,
  Destination,
  MealKey,
  MealsState,
  NlMunicipality,
  PerDiemCalculatorProps,
} from './types'
import {calcAT, calcDE, calcNL, calcUK} from './utils'

const PerDiemCalculator: FC<PerDiemCalculatorProps> = ({slice, context}) => {
  const domainCountry = useGetDomainCountry()
  const [country, setCountry] = useState<Country | null>(null)
  const [departure, setDeparture] = useState('')
  const [returnDate, setReturnDate] = useState('')
  const [destMode, setDestMode] = useState<Destination | null>(null)
  const [intlDest, setIntlDest] = useState('')
  const [priorDays, setPriorDays] = useState(0)
  const [atDistance, setAtDistance] = useState(50)
  const [nlMunicipality, setNlMunicipality] = useState<NlMunicipality>('yes')
  const [meals, setMeals] = useState<MealsState>({breakfast: false, lunch: false, dinner: false})
  const [result, setResult] = useState<CalcResult | null>(null)
  const [hasCalculated, setHasCalculated] = useState(false)
  const [warning, setWarning] = useState('')
  const [countryError, setCountryError] = useState(false)
  const [destModeError, setDestModeError] = useState(false)
  const [destinationError, setDestinationError] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const warningRef = useRef<HTMLDivElement>(null)
  const countryErrorRef = useRef<HTMLDivElement>(null)
  const destModeErrorRef = useRef<HTMLDivElement>(null)
  const destErrorRef = useRef<HTMLDivElement>(null)

  const homeCurrency = country === 'uk' ? 'GBP' : 'EUR'
  const destCurrencyInfo = destMode === 'international' && intlDest ? DESTINATION_CURRENCIES[intlDest] : null
  const needsConversion = destCurrencyInfo && destCurrencyInfo.code !== homeCurrency

  const {data: exchangeRateData, isLoading: exchangeRateLoading} = useMossExchangeRate(
    needsConversion ? homeCurrency : null,
    needsConversion ? destCurrencyInfo.code : null,
  )

  const exchangeRate = exchangeRateData?.rate ?? null

  const clearResult = () => setResult(null)

  const runCalculation = useCallback(
    (
      selectedCountry: Country,
      selectedDestMode: Destination,
      departureStr: string,
      returnStr: string,
      mealsState: MealsState,
      previousDays: number,
      distance: number,
      municipality: NlMunicipality,
      destination: string,
    ) => {
      const departureDate = new Date(departureStr)
      const returnDateParsed = new Date(returnStr)

      if (isNaN(departureDate.getTime()) || isNaN(returnDateParsed.getTime()) || returnDateParsed <= departureDate) {
        return
      }

      let calcResult: CalcResult

      if (selectedCountry === 'uk') {
        calcResult = calcUK(departureDate, returnDateParsed, selectedDestMode, destination, mealsState)
      } else if (selectedCountry === 'de') {
        calcResult = calcDE(departureDate, returnDateParsed, selectedDestMode, destination, mealsState, previousDays)
      } else if (selectedCountry === 'at') {
        calcResult = calcAT(departureDate, returnDateParsed, selectedDestMode, destination, mealsState, distance)
      } else {
        calcResult = calcNL(departureDate, returnDateParsed, selectedDestMode, destination, mealsState, municipality)
      }

      setResult(calcResult)
      setHasCalculated(true)

      if (calcResult.warn) {
        setWarning(calcResult.warn)
      }
    },
    [],
  )

  // Restore calculator state from URL params (shareable links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rawCountry = params.get('country')
    const rawDestMode = params.get('dest')
    const paramDeparture = params.get('dep') || ''
    const paramReturn = params.get('ret') || ''

    const validCountries: Country[] = ['uk', 'de', 'at', 'nl']
    const validDestModes: Destination[] = ['domestic', 'international']
    const paramCountry = validCountries.includes(rawCountry as Country) ? (rawCountry as Country) : null
    const paramDestMode = validDestModes.includes(rawDestMode as Destination) ? (rawDestMode as Destination) : null

    if (!paramCountry || !paramDestMode || !paramDeparture || !paramReturn) {
      return
    }

    const rawIntlDest = params.get('intlDest') || ''

    const paramIntlDest =
      paramDestMode === 'international' && rawIntlDest && getAvailableDestinations(paramCountry).has(rawIntlDest)
        ? rawIntlDest
        : ''

    const paramBreakfast = params.get('b') === '1'
    const paramLunch = params.get('l') === '1'
    const paramDinner = params.get('d') === '1'
    const paramPriorDays = Number(params.get('prior') || 0)
    const paramDistance = Number(params.get('dist') || 50)
    const paramMunicipality = (params.get('muni') || 'yes') as NlMunicipality

    setCountry(paramCountry)
    setDestMode(paramDestMode)
    setIntlDest(paramIntlDest)
    setDeparture(paramDeparture)
    setReturnDate(paramReturn)
    setMeals({breakfast: paramBreakfast, lunch: paramLunch, dinner: paramDinner})
    setPriorDays(paramPriorDays)
    setAtDistance(paramDistance)
    setNlMunicipality(paramMunicipality)

    setTimeout(() => {
      runCalculation(
        paramCountry,
        paramDestMode,
        paramDeparture,
        paramReturn,
        {breakfast: paramBreakfast, lunch: paramLunch, dinner: paramDinner},
        paramPriorDays,
        paramDistance,
        paramMunicipality,
        paramIntlDest,
      )

      setTimeout(() => {
        const el = document.getElementById('per-diem-results')
        el?.scrollIntoView({behavior: 'smooth', block: 'start'})
      }, 100)
    }, 0)
  }, [runCalculation])

  const pageType = context.pageUid || 'unknown'

  const trackInteraction = (element: string, detail: Record<string, unknown>) => {
    track({
      event: 'interaction',
      interaction: {element, location: 'per-diem-calculator', type: 'click', detail},
      page: {type: pageType, domainCountry},
    })
  }

  const toggleMeal = (key: MealKey) => {
    setMeals((prev) => ({...prev, [key]: !prev[key]}))
    clearResult()
  }

  const handleCalculate = () => {
    const noCountry = !country
    const noDestMode = !destMode
    const departureDate = new Date(departure)
    const returnDateValue = new Date(returnDate)
    const invalidDates = isNaN(departureDate.getTime()) || isNaN(returnDateValue.getTime())
    const returnBeforeDep = !invalidDates && returnDateValue <= departureDate
    const noIntlDest = destMode === 'international' && !intlDest

    const warnings: string[] = []

    if (noCountry) {
      warnings.push('Please select a country.')
    }

    if (noDestMode) {
      warnings.push('Please select a destination type.')
    }

    if (invalidDates) {
      warnings.push('Please enter valid dates.')
    }

    if (returnBeforeDep) {
      warnings.push('Return date must be after departure.')
    }

    if (noIntlDest) {
      warnings.push('Please select a travel destination.')
    }

    setCountryError(noCountry)
    setDestModeError(noDestMode)
    setDestinationError(noIntlDest)

    setWarning(
      invalidDates || returnBeforeDep ? warnings.find((w) => w.includes('date') || w.includes('Return')) || '' : '',
    )

    if (warnings.length > 0) {
      let firstErrorRef = destErrorRef

      if (noCountry) {
        firstErrorRef = countryErrorRef
      } else if (noDestMode) {
        firstErrorRef = destModeErrorRef
      } else if (invalidDates || returnBeforeDep) {
        firstErrorRef = warningRef
      }

      setTimeout(() => firstErrorRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'}), 50)
      return
    }

    trackInteraction('per-diem-calculate-button', {
      country,
      departure,
      returnDate,
      destMode,
      intlDest,
      meals,
      priorDays,
      atDistance,
      nlMunicipality,
    })

    setWarning('')
    setCountryError(false)
    setDestModeError(false)
    setDestinationError(false)
    let calcResult: CalcResult

    const dest = destMode!

    if (country === 'uk') {
      calcResult = calcUK(departureDate, returnDateValue, dest, intlDest, meals)
    } else if (country === 'de') {
      calcResult = calcDE(departureDate, returnDateValue, dest, intlDest, meals, priorDays)
    } else if (country === 'at') {
      calcResult = calcAT(departureDate, returnDateValue, dest, intlDest, meals, atDistance)
    } else {
      calcResult = calcNL(departureDate, returnDateValue, dest, intlDest, meals, nlMunicipality)
    }

    if (calcResult.warn) {
      setWarning(calcResult.warn)
    }

    setResult(calcResult)
    setHasCalculated(true)
    setTimeout(() => resultsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}), 50)
  }

  const formContent = (
    <>
      <CountrySelector
        country={country}
        hasError={countryError}
        errorRef={countryErrorRef}
        onSelect={(id) => {
          trackInteraction('per-diem-country-button', {country: id})
          setCountry(id)
          setCountryError(false)

          if (intlDest && !getAvailableDestinations(id).has(intlDest)) {
            setIntlDest('')
          }

          clearResult()
        }}
      />

      <DestinationSelector
        destMode={destMode}
        intlDest={intlDest}
        country={country ?? ''}
        rateNote={result?.rateNote}
        hasError={destinationError}
        hasDestModeError={destModeError}
        errorRef={destErrorRef}
        destModeErrorRef={destModeErrorRef}
        onDestModeChange={(mode) => {
          trackInteraction('per-diem-destination-button', {destMode: mode})
          setDestMode(mode)
          setDestModeError(false)

          if (mode === 'domestic') {
            setIntlDest('')
          }

          setDestinationError(false)
          clearResult()
        }}
        onIntlDestChange={(dest) => {
          setIntlDest(dest)
          setDestinationError(false)
          clearResult()
        }}
      />

      <TripDetailsForm
        country={country}
        departure={departure}
        returnDate={returnDate}
        priorDays={priorDays}
        atDistance={atDistance}
        nlMunicipality={nlMunicipality}
        warning={warning}
        warningRef={warningRef}
        onDepartureChange={(v) => {
          trackInteraction('per-diem-departure-input', {departure: v})
          setDeparture(v)
          setWarning('')
          clearResult()
        }}
        onReturnChange={(v) => {
          trackInteraction('per-diem-return-input', {returnDate: v})
          setReturnDate(v)
          setWarning('')
          clearResult()
        }}
        onPriorDaysChange={(v) => {
          setPriorDays(v)
          clearResult()
        }}
        onAtDistanceChange={(v) => {
          setAtDistance(v)
          clearResult()
        }}
        onNlMunicipalityChange={(v) => {
          setNlMunicipality(v)
          clearResult()
        }}
      />
    </>
  )

  const howItWorksRef = useRef(null)
  const howItWorksInView = useInView(howItWorksRef, {amount: 0.1, once: true})
  const {scrollYProgress: howItWorksScroll} = useScroll({target: howItWorksRef, offset: ['start end', 'end start']})
  const line1Width = useTransform(howItWorksScroll, [0.2, 0.3], ['0%', '100%'])
  const line2Width = useTransform(howItWorksScroll, [0.3, 0.4], ['0%', '100%'])

  const stepVariants = {
    hidden: {opacity: 0},
    visible: (i: number) => ({
      opacity: 1,
      transition: {delay: i * 0.2, duration: 0.6, ease: 'easeIn'},
    }),
  }

  const STEP_CARDS = [
    {
      title: 'Enter the trip details',
      bg: 'bg-[#FBFBFB]',
      items: [
        'Pick your country, departure date, and return date',
        'Choose domestic or international travel',
        'Covers UK, Germany, Austria and the Netherlands',
        'Uses official 2026 per diem rates per country',
        'Built for multi-country travel programmes',
      ],
    },
    {
      title: 'Mark employer-provided meals',
      bg: 'bg-[#FBFBFB]',
      items: [
        'Tick which meals are covered by your company (breakfast, lunch, dinner)',
        'Automatically deducts those meals and shows claimable expenses only',
        'Same deductions apply to every full day',
        'First and last day exceptions are not yet supported, check those against your travel policy',
      ],
    },
    {
      title: 'Get a compliant allowance ready for payroll',
      bg: 'bg-moss-blue-light',
      items: [
        'Itemised breakdown of meal allowances, accommodation caps and incidentals',
        'Day-by-day log of claimable amounts',
        'Plain-language summary of which tax rules apply',
        'Ready to export into your spend management or pre-accounting tool',
      ],
    },
  ]

  const howItWorks = (
    <Container backgroundColor="Moss White" className="!py-10 md:!py-20">
      <div
        ref={howItWorksRef}
        className="mx-auto flex w-full max-w-[1200px] flex-col gap-y-12 px-5 md:px-10 lg:px-5 xl:px-0"
      >
        <div className="flex flex-col gap-3 md:gap-6 md:pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-moss-grey-400">
            Simple. Compliant. Fast.
          </p>
          <h2 className="text-[32px] font-normal leading-9 tracking-[0.32px] text-[#131212] md:text-[40px] md:leading-[48px]">
            How Moss per diem calculator works
          </h2>
        </div>

        <div className="relative mb-6 hidden grid-cols-3 gap-6 md:grid">
          {['Step 1', 'Step 2', 'Step 3'].map((label, i) => (
            <div key={label} className="z-10 flex justify-center">
              <motion.span
                className={`text-button-s flex h-8 items-center justify-center rounded-[4px] px-4 font-semibold uppercase ${
                  i === 2 ? 'bg-moss-blue text-moss-white' : 'bg-moss-grey-100 text-black'
                }`}
                custom={i}
                variants={stepVariants}
                initial="hidden"
                animate={howItWorksInView ? 'visible' : 'hidden'}
              >
                {label}
              </motion.span>
            </div>
          ))}
          <div className="absolute left-[calc(16.67%+48px)] right-[calc(50%+48px)] top-1/2 -translate-y-1/2">
            <motion.div style={{width: line1Width}} className="h-0.5 bg-moss-grey-150" />
          </div>
          <div className="absolute left-[calc(50%+48px)] right-[calc(16.67%+48px)] top-1/2 -translate-y-1/2">
            <motion.div style={{width: line2Width}} className="h-0.5 bg-moss-grey-150" />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {STEP_CARDS.map((card, i) => (
            <motion.div
              key={card.title}
              className={`flex flex-col rounded-lg border border-[#000]/[10%] ${card.bg} overflow-hidden p-6`}
              custom={i}
              variants={stepVariants}
              initial="hidden"
              animate={howItWorksInView ? 'visible' : 'hidden'}
            >
              <div className="mb-4 flex justify-center md:hidden">
                <span
                  className={`text-button-s flex h-8 items-center justify-center rounded-[4px] px-4 font-semibold uppercase ${
                    i === 2 ? 'bg-moss-blue text-moss-white' : 'bg-moss-grey-100 text-black'
                  }`}
                >
                  Step {i + 1}
                </span>
              </div>
              <div className="flex flex-1 flex-col">
                <h3 className="text-[18px] font-medium leading-6 text-moss-black">{card.title}</h3>
                <ul className="mt-4 space-y-2 text-[14px] leading-5 text-moss-grey-400">
                  {card.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-sm bg-grey-150" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="flex justify-center">
          <a
            href="#per-diem-calculator"
            className="text-button-s group relative flex h-12 items-center justify-center gap-2 rounded-[4px] bg-green-neon px-8 py-3 uppercase text-black transition-all"
          >
            <span className="z-10">Calculate for free</span>
            <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%] group-hover:bg-moss-white/[20%]" />
          </a>
        </div>
      </div>
    </Container>
  )

  return (
    <>
      {howItWorks}

      <Container
        backgroundColor="Dark Green"
        className="!py-[30px] md:!py-[90px]"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        id="per-diem-calculator"
      >
        <motion.div
          className="mx-auto w-full max-w-[750px] px-6"
          initial={{opacity: 0, translateY: 20}}
          whileInView={{opacity: 1, translateY: 0}}
          viewport={{amount: 0.1, once: true}}
          transition={{duration: 0.7, ease: 'easeInOut'}}
        >
          <div className="overflow-visible rounded-2xl border border-grey-150 bg-moss-white p-8 shadow-elevation-2">
            <h2 className="mb-1 text-[24px] font-medium leading-8 tracking-[0.32px] text-[#131212] md:text-[28px] md:leading-9">
              Per diem calculator
            </h2>
            <p className="mb-6 text-14 text-grey-450">
              Calculate tax-free travel allowances with official 2026 rates for Germany, UK, Austria and the
              Netherlands.
            </p>
            <div className="overflow-visible rounded-lg border border-grey-150 bg-moss-white">
              <CardHeader step={1} label="Trip details" />
              {formContent}
            </div>

            <div className="mt-6">
              <MealSelector meals={meals} onToggle={toggleMeal} />
            </div>

            {result ? (
              <div className="mt-6">
                <ResultsDisplay
                  ref={resultsRef}
                  result={result}
                  country={country!}
                  destMode={destMode!}
                  intlDest={intlDest}
                  exchangeRate={exchangeRate}
                  exchangeRateLoading={exchangeRateLoading}
                  destCurrencyInfo={destCurrencyInfo ?? null}
                  onCopyLink={() => {
                    trackInteraction('per-diem-copy-link', {country, destMode, intlDest})
                    const params = new URLSearchParams()
                    params.set('country', country!)
                    params.set('dest', destMode!)

                    if (intlDest) {
                      params.set('intlDest', intlDest)
                    }

                    params.set('dep', departure)
                    params.set('ret', returnDate)
                    params.set('b', meals.breakfast ? '1' : '0')
                    params.set('l', meals.lunch ? '1' : '0')
                    params.set('d', meals.dinner ? '1' : '0')

                    if (country === 'de' && priorDays > 0) {
                      params.set('prior', String(priorDays))
                    }

                    if (country === 'at') {
                      params.set('dist', String(atDistance))
                    }

                    if (country === 'nl') {
                      params.set('muni', nlMunicipality)
                    }

                    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`
                    navigator.clipboard.writeText(url).catch(() => {})
                  }}
                  onExport={() => {
                    trackInteraction('per-diem-export-pdf', {country, destMode, intlDest})
                  }}
                />
              </div>
            ) : (
              <div className="mt-6 overflow-hidden rounded-lg border border-grey-150 bg-moss-white">
                <CardHeader step={3} label="Your results" />
                <div className="p-6">
                  <p className="mb-4 text-center text-14 text-grey-450">
                    {hasCalculated
                      ? 'Update your selections and recalculate to see new results.'
                      : 'Fill in your trip details and meals, then calculate to see your allowance.'}
                  </p>
                  <button
                    onClick={handleCalculate}
                    className="text-button-s group relative flex h-12 w-full items-center justify-center gap-2 rounded-[4px] bg-green-neon px-4 py-3 uppercase text-black transition-all"
                  >
                    <span className="z-10">Calculate per diem</span>
                    <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%] group-hover:bg-moss-white/[20%]" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </Container>
    </>
  )
}

export default PerDiemCalculator
